import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    ArrowLeft,
    ArrowRight,
    Download,
    MessageCircle,
    TrendingUp,
    TrendingDown,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Activity,
    Lightbulb,
    ClipboardList,
    FileText,
    Calendar,
    Trash2,
    Share2,
    Printer,
    Mail,
    Copy,
    ChevronUp,
    ChevronDown,
    Minus,
    QrCode,
    StickyNote,
    History,
    BarChart3,
    Shield,
    User,
    Building2,
    Clock,
    Loader2,
    AlertCircle,
    Salad
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Gauge, HelpCircle, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mini sparkline chart component
const Sparkline = ({ data, color = '#14b8a6', width = 60, height = 20 }) => {
    if (!data || data.length < 2) return null;
    
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    
    const points = data.map((val, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((val - min) / range) * height;
        return `${x},${y}`;
    }).join(' ');
    
    return (
        <svg width={width} height={height} className="inline-block ltr:ml-2 rtl:mr-2">
            <polyline
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                points={points}
            />
            <circle
                cx={(data.length - 1) / (data.length - 1) * width}
                cy={height - ((data[data.length - 1] - min) / range) * height}
                r="2"
                fill={color}
            />
        </svg>
    );
};

// Visual reference range bar component
const ReferenceRangeBar = ({ value, min, max, status }) => {
    const numValue = parseFloat(value) || 0;
    const numMin = parseFloat(min) || 0;
    const numMax = parseFloat(max) || 100;
    
    // Calculate position (with some padding for out-of-range values)
    const rangeSpan = numMax - numMin;
    const padding = rangeSpan * 0.2;
    const displayMin = numMin - padding;
    const displayMax = numMax + padding;
    const displayRange = displayMax - displayMin;
    
    const position = Math.max(0, Math.min(100, ((numValue - displayMin) / displayRange) * 100));
    const normalStart = ((numMin - displayMin) / displayRange) * 100;
    const normalEnd = ((numMax - displayMin) / displayRange) * 100;
    
    const statusColors = {
        normal: '#10b981',
        abnormal: '#f59e0b',
        critical: '#ef4444'
    };
    
    return (
        <div className="w-full mt-2">
            <div className="relative h-3 bg-stone-200 rounded-full overflow-hidden">
                {/* Normal range zone */}
                <div 
                    className="absolute h-full bg-emerald-200"
                    style={{ left: `${normalStart}%`, width: `${normalEnd - normalStart}%` }}
                />
                {/* Value indicator */}
                <div 
                    className="absolute w-3 h-3 rounded-full border-2 border-white shadow-md transform -translate-x-1/2"
                    style={{ 
                        left: `${position}%`, 
                        backgroundColor: statusColors[status] || statusColors.normal 
                    }}
                />
            </div>
            <div className="flex justify-between text-xs text-stone-500 mt-1">
                <span>{numMin}</span>
                <span>{numMax}</span>
            </div>
        </div>
    );
};

// Parse reference range string
const parseReferenceRange = (rangeStr) => {
    if (!rangeStr) return { min: 0, max: 100 };
    const match = rangeStr.match(/(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
    if (match) {
        return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
    }
    return { min: 0, max: 100 };
};

// Group lab values by category
const groupByCategory = (labValues) => {
    const categories = {
        'Lipid Panel': ['Cholesterol', 'LDL', 'HDL', 'Triglycerides', 'VLDL'],
        'Complete Blood Count': ['Hemoglobin', 'Hematocrit', 'RBC', 'WBC', 'Platelets', 'MCV', 'MCH', 'MCHC'],
        'Liver Function': ['ALT', 'AST', 'ALP', 'Bilirubin', 'Albumin', 'GGT'],
        'Kidney Function': ['Creatinine', 'BUN', 'Urea', 'GFR', 'Uric Acid'],
        'Thyroid Panel': ['TSH', 'T3', 'T4', 'Free T3', 'Free T4'],
        'Glucose & Diabetes': ['Glucose', 'HbA1c', 'Fasting Glucose', 'Blood Sugar'],
        'Electrolytes': ['Sodium', 'Potassium', 'Chloride', 'Calcium', 'Magnesium', 'Phosphorus'],
        'Other': []
    };
    
    const grouped = {};
    const assigned = new Set();
    
    for (const [category, keywords] of Object.entries(categories)) {
        grouped[category] = [];
        for (const value of labValues || []) {
            if (assigned.has(value.name)) continue;
            const matchesCategory = keywords.some(keyword => 
                value.name.toLowerCase().includes(keyword.toLowerCase())
            );
            if (matchesCategory) {
                grouped[category].push(value);
                assigned.add(value.name);
            }
        }
    }
    
    // Add remaining to "Other"
    for (const value of labValues || []) {
        if (!assigned.has(value.name)) {
            grouped['Other'].push(value);
        }
    }
    
    // Remove empty categories
    return Object.fromEntries(
        Object.entries(grouped).filter(([_, values]) => values.length > 0)
    );
};

// Sort by priority (critical > abnormal > normal)
const sortByPriority = (labValues) => {
    const priority = { critical: 0, abnormal: 1, normal: 2 };
    return [...(labValues || [])].sort((a, b) => 
        (priority[a.status] ?? 3) - (priority[b.status] ?? 3)
    );
};

const ReportPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const BackIcon = isRTL ? ArrowRight : ArrowLeft;
    const dateLocale = isRTL ? ar : enUS;
    const { reportId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [notes, setNotes] = useState('');
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareEmail, setShareEmail] = useState('');
    const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
    const [sharePhone, setSharePhone] = useState('');
    const [sharingLink, setSharingLink] = useState(false);
    const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'priority'
    const [historicalData, setHistoricalData] = useState({});
    const printRef = useRef(null);

    // Status translations
    const statusText = {
        normal: t('report.normal'),
        abnormal: t('report.abnormal'),
        critical: t('report.critical')
    };
    
    const trendText = {
        increasing: t('report.increasing'),
        decreasing: t('report.decreasing'),
        stable: t('report.stable'),
        new: t('report.new')
    };
    
    // Helper to get localized content from report
    const getLocalizedContent = (field) => {
        if (!report) return '';
        if (isRTL) {
            // Try Arabic version first, fallback to default
            return report[`${field}_ar`] || report[field] || '';
        }
        return report[field] || '';
    };
    
    const getLocalizedArray = (field) => {
        if (!report) return [];
        if (isRTL) {
            // Try Arabic version first, fallback to default
            return report[`${field}_ar`] || report[field] || [];
        }
        return report[field] || [];
    };

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const response = await axios.get(`${API}/reports/${reportId}`);
                setReport(response.data);
                setNotes(response.data.notes || '');
                
                if (response.data.status === 'processing') {
                    setLoading(false);
                    setTimeout(fetchReport, 4000);
                    return;
                }
                
                // Fetch historical data for trends
                const historyResponse = await axios.get(`${API}/reports`);
                
                // Build historical data for sparklines
                const history = {};
                for (const r of historyResponse.data.reports || []) {
                    for (const val of r.lab_values || []) {
                        if (!history[val.name]) history[val.name] = [];
                        history[val.name].push({
                            value: parseFloat(val.value) || 0,
                            date: r.upload_date
                        });
                    }
                }
                // Sort by date and keep only values
                for (const name of Object.keys(history)) {
                    history[name] = history[name]
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map(h => h.value)
                        .slice(-5); // Last 5 values
                }
                setHistoricalData(history);
                
            } catch (error) {
                console.error('Failed to fetch report:', error);
                toast.error('Failed to load report');
                navigate('/dashboard');
            } finally {
                setLoading(false);
            }
        };

        if (user && reportId) fetchReport();
    }, [user, reportId, navigate]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            // Pass language parameter to generate PDF in current language
            const response = await axios.get(`${API}/reports/${reportId}/pdf?lang=${i18n.language}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `lab_report_${reportId.slice(0, 8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success(t('report.pdfDownloaded'));
        } catch (error) {
            console.error('Download error:', error);
            toast.error(t('errors.generic'));
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('report.deleteConfirm'))) return;
        
        setDeleting(true);
        try {
            await axios.delete(`${API}/reports/${reportId}`);
            toast.success(t('notifications.deleteSuccess'));
            navigate('/dashboard');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(t('errors.generic'));
        } finally {
            setDeleting(false);
        }
    };

    const handleSaveNotes = async () => {
        try {
            await axios.put(`${API}/reports/${reportId}/notes`, { notes });
            toast.success(t('notifications.saveSuccess'));
        } catch (error) {
            toast.error(t('errors.generic'));
        }
    };

    const handleShare = async () => {
        if (!shareEmail) {
            toast.error(t('errors.validation'));
            return;
        }
        // In production, this would send an email with the report link
        toast.success(t('notifications.shareSuccess'));
        setShareDialogOpen(false);
        setShareEmail('');
    };

    const buildSecureShareLink = async () => {
        const res = await axios.post(`${API}/reports/${reportId}/share`, {});
        return `${window.location.origin}/shared/${res.data.share_token}`;
    };

    const handleWhatsAppLinkShare = async () => {
        setSharingLink(true);
        try {
            const url = await buildSecureShareLink();
            const dateStr = report?.upload_date ? new Date(report.upload_date).toLocaleDateString() : '';
            const msg = encodeURIComponent(isRTL
                ? `🔬 تقرير التحاليل الطبية — نتيجتي الطبية\n📅 ${dateStr}\n\nيمكنك الاطلاع على التقرير من الرابط التالي (صالح لمدة 7 أيام):\n${url}`
                : `🔬 Lab Report — Natijaty\n📅 ${dateStr}\n\nView the report here (valid for 7 days):\n${url}`);
            let digits = sharePhone.replace(/\D/g, '');
            if (digits.startsWith('00')) digits = digits.slice(2);
            if (digits.startsWith('05')) digits = '966' + digits.slice(1);
            else if (digits.length === 9 && digits.startsWith('5')) digits = '966' + digits;
            window.open(digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
            setShareDialogOpen(false);
        } catch (e) {
            toast.error(isRTL ? 'تعذر إنشاء رابط المشاركة' : 'Failed to create share link');
        } finally {
            setSharingLink(false);
        }
    };

    const handleCopySecureLink = async () => {
        try {
            const url = await buildSecureShareLink();
            await navigator.clipboard.writeText(url);
            toast.success(isRTL ? 'تم نسخ الرابط الآمن (صالح 7 أيام)' : 'Secure link copied (valid 7 days)');
        } catch (e) {
            toast.error(isRTL ? 'تعذر نسخ الرابط' : 'Failed to copy link');
        }
    };

    const handleWhatsAppShare = async () => {
        setSharingWhatsApp(true);
        try {
            // Download the PDF first with current language
            const response = await axios.get(`${API}/reports/${reportId}/pdf?lang=${i18n.language}`, {
                responseType: 'blob'
            });
            
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const fileName = `lab_report_${reportId.slice(0, 8)}.pdf`;
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            
            const reportTitle = isRTL ? 'تقرير التحليل المخبري' : 'Lab Report';
            const reportType = report?.panel_type || (isRTL ? 'نتائج طبية' : 'Medical Results');
            
            // Helper function for download and WhatsApp fallback
            const downloadAndOpenWhatsApp = () => {
                const url = window.URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                
                // Open WhatsApp with a message
                const message = encodeURIComponent(isRTL 
                    ? `📋 ${reportTitle}\n\nقمت بتحميل ملف PDF وسأرفقه في هذه الرسالة.\n\nرقم التقرير: ${reportId.slice(0, 8).toUpperCase()}`
                    : `📋 ${reportTitle}\n\nI've downloaded the PDF report and will attach it to this message.\n\nReport ID: ${reportId.slice(0, 8).toUpperCase()}`);
                window.open(`https://wa.me/?text=${message}`, '_blank');
                
                toast.success(t('report.pdfDownloaded'));
            };
            
            // Check if Web Share API with files is supported
            if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
                try {
                    // Use Web Share API to share the file
                    await navigator.share({
                        title: reportTitle,
                        text: `📋 ${reportTitle} - ${reportType}\n${isRTL ? 'رقم التقرير' : 'Report ID'}: ${reportId.slice(0, 8).toUpperCase()}`,
                        files: [pdfFile]
                    });
                    toast.success(t('report.shareSuccess'));
                } catch (shareError) {
                    // User cancelled or share failed - fallback to download
                    if (shareError.name !== 'AbortError') {
                        console.log('Share API failed, falling back to download:', shareError);
                        downloadAndOpenWhatsApp();
                    }
                }
            } else {
                // Fallback: Download the PDF and prompt user
                downloadAndOpenWhatsApp();
            }
            setShareDialogOpen(false);
        } catch (error) {
            console.error('WhatsApp share error:', error);
            toast.error(isRTL ? 'فشل مشاركة التقرير. يرجى تحميل الـ PDF يدوياً.' : 'Failed to share report. Please try downloading the PDF manually.');
        } finally {
            setSharingWhatsApp(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case 'normal':
                return {
                    icon: CheckCircle2,
                    color: 'text-emerald-600',
                    bg: 'bg-emerald-50',
                    border: 'border-emerald-200',
                    label: 'Normal',
                    flagIcon: null
                };
            case 'abnormal':
                return {
                    icon: AlertTriangle,
                    color: 'text-amber-600',
                    bg: 'bg-amber-50',
                    border: 'border-amber-200',
                    label: 'Abnormal',
                    flagIcon: null
                };
            case 'critical':
                return {
                    icon: XCircle,
                    color: 'text-red-600',
                    bg: 'bg-red-50',
                    border: 'border-red-200',
                    label: 'Critical',
                    flagIcon: null
                };
            default:
                return {
                    icon: Activity,
                    color: 'text-stone-600',
                    bg: 'bg-stone-50',
                    border: 'border-stone-200',
                    label: 'Unknown',
                    flagIcon: null
                };
        }
    };

    // Determine if value is high or low
    const getValueFlag = (value, referenceRange) => {
        const { min, max } = parseReferenceRange(referenceRange);
        const numValue = parseFloat(value) || 0;
        if (numValue < min) return 'low';
        if (numValue > max) return 'high';
        return null;
    };

    if (loading) {
        return (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Skeleton className="h-10 w-48 mb-6" />
                <Skeleton className="h-48 rounded-2xl mb-6" />
                <Skeleton className="h-96 rounded-2xl" />
            </div>
        );
    }

    if (!report) return null;

    if (report.status === 'processing') {
        return (
            <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="report-processing">
                <Loader2 className="w-10 h-10 animate-spin text-teal-500 mx-auto mb-4" />
                <p className="text-stone-700 font-medium text-lg">
                    {isRTL ? 'جاري تحليل تقريرك بالذكاء الاصطناعي...' : 'Your report is being analyzed by AI...'}
                </p>
                <p className="text-stone-400 text-sm mt-2">
                    {isRTL ? 'قد يستغرق ذلك دقيقة أو دقيقتين حسب عدد الصفحات — سيتم تحديث الصفحة تلقائياً' : 'This may take a minute or two depending on page count — the page will refresh automatically'}
                </p>
            </div>
        );
    }

    if (report.status === 'failed') {
        return (
            <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="report-failed">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
                <p className="text-stone-700 font-medium text-lg">
                    {isRTL ? 'تعذر تحليل هذا التقرير' : 'This report could not be analyzed'}
                </p>
                <p className="text-stone-400 text-sm mt-2">
                    {isRTL ? 'لم يتم خصم أي رصيد — يمكنك إعادة رفع الملفات مرة أخرى' : 'No credit was used — you can upload the files again'}
                </p>
                <Link to="/upload">
                    <Button className="mt-6 bg-teal-500 hover:bg-teal-600 rounded-full px-6">
                        {isRTL ? 'إعادة المحاولة' : 'Try again'}
                    </Button>
                </Link>
            </div>
        );
    }

    const normalCount = report.lab_values?.filter(v => v.status === 'normal').length || 0;
    const abnormalCount = report.lab_values?.filter(v => v.status === 'abnormal').length || 0;
    const criticalCount = report.lab_values?.filter(v => v.status === 'critical').length || 0;
    
    const groupedValues = groupByCategory(report.lab_values);
    const sortedValues = sortByPriority(report.lab_values);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="report-page" ref={printRef}>
            {/* Professional Header with Logo */}
            <div className="bg-white rounded-2xl shadow-lg shadow-stone-200/50 p-6 mb-8 print:shadow-none print:border print:border-stone-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    {/* Logo and Title */}
                    <div className="flex items-center gap-4">
                        <img 
                            src="/logo.svg" 
                            alt="نتيجتي الطبية" 
                            className="h-14 w-auto"
                        />
                        <div className="border-l border-stone-200 pl-4">
                            <h1 className="text-xl font-bold text-stone-800">
                                {report.panel_type || 'Laboratory Report'}
                            </h1>
                            <p className="text-sm text-stone-500">نتيجتي الطبية - My Medical Results</p>
                        </div>
                    </div>
                    
                    {/* Report Info */}
                    <div className="text-right">
                        <div className="flex items-center gap-2 text-sm text-stone-600 mb-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(report.upload_date), 'MMMM d, yyyy')}
                        </div>
                        <p className="text-xs text-stone-400 font-mono">
                            Report ID: {reportId.slice(0, 8).toUpperCase()}
                        </p>
                    </div>
                </div>
                
                {/* Patient Info Bar */}
                <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-stone-400" />
                        <span className="text-stone-600">{user?.name || 'Patient'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-stone-400" />
                        <span className="text-stone-600">{user?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <Shield className="w-4 h-4 text-teal-500" />
                        <span className="text-teal-600 text-xs">Verified & Secure</span>
                    </div>
                </div>
            </div>

            {/* Action Buttons - Hidden on Print */}
            <div className="flex flex-wrap justify-between items-center gap-4 mb-8 print:hidden">
                <div className="flex items-center gap-2">
                    <Link to="/dashboard">
                        <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                    </Link>
                    <span className="text-stone-500 text-sm">Back to Dashboard</span>
                </div>
                <div className="flex items-center gap-2">
                    <Link to={`/chat?report=${reportId}`}>
                        <Button variant="outline" className="rounded-full" data-testid="ask-ai-btn">
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Ask AI
                        </Button>
                    </Link>
                    <Button
                        variant="outline"
                        onClick={() => setShareDialogOpen(true)}
                        className="rounded-full"
                        data-testid="share-btn"
                    >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handlePrint}
                        className="rounded-full"
                        data-testid="print-btn"
                    >
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={downloading}
                        className="bg-teal-500 hover:bg-teal-600 text-white rounded-full"
                        data-testid="download-pdf-btn"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {downloading ? 'Downloading...' : 'PDF'}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleDelete}
                        disabled={deleting}
                        className="text-stone-400 hover:text-red-600 rounded-full"
                        data-testid="delete-report-btn"
                    >
                        <Trash2 className="w-5 h-5" />
                    </Button>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 text-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-700">{normalCount}</p>
                    <p className="text-sm text-emerald-600">Normal</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 text-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-amber-700">{abnormalCount}</p>
                    <p className="text-sm text-amber-600">Abnormal</p>
                </div>
                <div className="p-4 bg-red-50 rounded-2xl border border-red-100 text-center">
                    <XCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-700">{criticalCount}</p>
                    <p className="text-sm text-red-600">Critical</p>
                </div>
            </div>

            {/* Overall Health Score & Urgency & Severity */}
            <div className={`grid grid-cols-1 md:grid-cols-2 ${typeof report.severity_score === 'number' ? 'lg:grid-cols-3' : ''} gap-4 mb-8`}>
                {/* Overall Health Score */}
                <div className={`p-5 rounded-2xl border ${
                    report.overall_health_score === 'Excellent' ? 'bg-emerald-50 border-emerald-200' :
                    report.overall_health_score === 'Good' ? 'bg-teal-50 border-teal-200' :
                    report.overall_health_score === 'Fair' ? 'bg-amber-50 border-amber-200' :
                    'bg-orange-50 border-orange-200'
                }`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-stone-500 mb-1">{t('report.overallHealthScore')}</p>
                            <p className={`text-2xl font-bold ${
                                report.overall_health_score === 'Excellent' ? 'text-emerald-700' :
                                report.overall_health_score === 'Good' ? 'text-teal-700' :
                                report.overall_health_score === 'Fair' ? 'text-amber-700' :
                                'text-orange-700'
                            }`}>{
                                report.overall_health_score === 'Excellent' ? t('report.excellent') :
                                report.overall_health_score === 'Good' ? t('report.good') :
                                report.overall_health_score === 'Fair' ? t('report.fair') :
                                t('report.needsAttention')
                            }</p>
                        </div>
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                            report.overall_health_score === 'Excellent' ? 'bg-emerald-200' :
                            report.overall_health_score === 'Good' ? 'bg-teal-200' :
                            report.overall_health_score === 'Fair' ? 'bg-amber-200' :
                            'bg-orange-200'
                        }`}>
                            {report.overall_health_score === 'Excellent' || report.overall_health_score === 'Good' ? (
                                <CheckCircle2 className={`w-8 h-8 ${report.overall_health_score === 'Excellent' ? 'text-emerald-600' : 'text-teal-600'}`} />
                            ) : (
                                <AlertTriangle className={`w-8 h-8 ${report.overall_health_score === 'Fair' ? 'text-amber-600' : 'text-orange-600'}`} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Urgency Level */}
                <div className={`p-5 rounded-2xl border ${
                    report.urgency_level === 'routine' ? 'bg-emerald-50 border-emerald-200' :
                    report.urgency_level === 'monitor' ? 'bg-blue-50 border-blue-200' :
                    report.urgency_level === 'prompt_review' ? 'bg-amber-50 border-amber-200' :
                    'bg-red-50 border-red-200'
                }`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-stone-500 mb-1">{t('report.recommendedAction')}</p>
                            <p className={`text-xl font-bold ${
                                report.urgency_level === 'routine' ? 'text-emerald-700' :
                                report.urgency_level === 'monitor' ? 'text-blue-700' :
                                report.urgency_level === 'prompt_review' ? 'text-amber-700' :
                                'text-red-700'
                            }`}>
                                {report.urgency_level === 'routine' ? t('report.routineFollowUp') :
                                 report.urgency_level === 'monitor' ? t('report.monitorRetest') :
                                 report.urgency_level === 'prompt_review' ? t('report.seeDoctorSoon') :
                                 t('report.urgentReview')}
                            </p>
                            <p className="text-xs text-stone-500 mt-1">
                                {report.urgency_level === 'routine' ? t('report.scheduleNextCheckup') :
                                 report.urgency_level === 'monitor' ? t('report.retestIn4to8Weeks') :
                                 report.urgency_level === 'prompt_review' ? t('report.scheduleThisWeek') :
                                 t('report.contactTodday')}
                            </p>
                        </div>
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                            report.urgency_level === 'routine' ? 'bg-emerald-200' :
                            report.urgency_level === 'monitor' ? 'bg-blue-200' :
                            report.urgency_level === 'prompt_review' ? 'bg-amber-200' :
                            'bg-red-200'
                        }`}>
                            <Clock className={`w-7 h-7 ${
                                report.urgency_level === 'routine' ? 'text-emerald-600' :
                                report.urgency_level === 'monitor' ? 'text-blue-600' :
                                report.urgency_level === 'prompt_review' ? 'text-amber-600' :
                                'text-red-600'
                            }`} />
                        </div>
                    </div>
                </div>

                {/* Severity Score (1-10) */}
                {typeof report.severity_score === 'number' && (
                    <div className={`p-5 rounded-2xl border ${
                        report.severity_score <= 3 ? 'bg-emerald-50 border-emerald-200' :
                        report.severity_score <= 6 ? 'bg-amber-50 border-amber-200' :
                        'bg-red-50 border-red-200'
                    }`} data-testid="severity-score-card">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-stone-500 mb-1">{isRTL ? 'درجة الخطورة' : 'Severity Score'}</p>
                                <p className={`text-2xl font-bold ${
                                    report.severity_score <= 3 ? 'text-emerald-700' :
                                    report.severity_score <= 6 ? 'text-amber-700' :
                                    'text-red-700'
                                }`}>
                                    {report.severity_score}<span className="text-sm font-normal text-stone-400">/10</span>
                                </p>
                                <p className="text-xs text-stone-500 mt-1">
                                    {report.severity_score <= 3 ? (isRTL ? 'طبيعي / انحرافات طفيفة' : 'Normal / mild deviations') :
                                     report.severity_score <= 6 ? (isRTL ? 'متوسط — يُنصح بالمتابعة' : 'Moderate — monitoring advised') :
                                     (isRTL ? 'مهم — يُنصح باستشارة الطبيب' : 'Significant — consult your doctor')}
                                </p>
                            </div>
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                                report.severity_score <= 3 ? 'bg-emerald-200' :
                                report.severity_score <= 6 ? 'bg-amber-200' :
                                'bg-red-200'
                            }`}>
                                <Gauge className={`w-8 h-8 ${
                                    report.severity_score <= 3 ? 'text-emerald-600' :
                                    report.severity_score <= 6 ? 'text-amber-600' :
                                    'text-red-600'
                                }`} />
                            </div>
                        </div>
                        <div className="mt-3 h-2 bg-white/70 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full ${
                                    report.severity_score <= 3 ? 'bg-emerald-500' :
                                    report.severity_score <= 6 ? 'bg-amber-500' :
                                    'bg-red-500'
                                }`}
                                style={{ width: `${report.severity_score * 10}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Follow-up symptom questions */}
            {(report.follow_up_questions_ar?.length > 0 || report.follow_up_questions?.length > 0) && (
                <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8 print:shadow-none print:border print:border-stone-200" data-testid="follow-up-questions-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                                <HelpCircle className="w-4 h-4 text-teal-600" />
                            </div>
                            {isRTL ? 'أسئلة لمتابعة حالتك' : 'Follow-up Questions'}
                        </CardTitle>
                        <p className="text-xs text-stone-500 mt-1">
                            {isRTL
                                ? 'نتائج التحاليل نصف الصورة فقط — اضغط على أي سؤال لمناقشته مع المساعد الذكي'
                                : 'Lab results are only half the picture — tap a question to discuss it with the AI assistant'}
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {((isRTL && report.follow_up_questions_ar?.length ? report.follow_up_questions_ar : report.follow_up_questions) || []).map((q, idx) => (
                            <Link
                                key={idx}
                                to={`/chat?report=${reportId}&q=${encodeURIComponent(q)}`}
                                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-teal-100 bg-teal-50/50 hover:bg-teal-50 hover:border-teal-300 transition-colors group"
                                data-testid={`follow-up-question-${idx}`}
                            >
                                <span className="text-sm text-stone-700">{q}</span>
                                <MessageCircle className="w-4 h-4 text-teal-500 shrink-0 group-hover:scale-110 transition-transform" />
                            </Link>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Recommended specialists */}
            {report.recommended_specialists?.length > 0 && (
                <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8 print:shadow-none print:border print:border-stone-200" data-testid="recommended-specialists-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                                <Stethoscope className="w-4 h-4 text-sky-600" />
                            </div>
                            {isRTL ? 'التخصصات الطبية الموصى بها' : 'Recommended Specialists'}
                        </CardTitle>
                        <p className="text-xs text-stone-500 mt-1">
                            {isRTL
                                ? 'بناءً على نتائج تحاليلك، قد يفيدك استشارة هذه التخصصات'
                                : 'Based on your results, consulting these specialties may be helpful'}
                        </p>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {report.recommended_specialists.map((sp, idx) => {
                            const urg = (sp.urgency || '').toLowerCase();
                            const style = urg === 'high'
                                ? { border: 'border-red-200', bg: 'bg-red-50/50', badge: 'bg-red-100 text-red-700', label: isRTL ? 'أولوية عالية' : 'High priority' }
                                : urg === 'medium'
                                ? { border: 'border-amber-200', bg: 'bg-amber-50/50', badge: 'bg-amber-100 text-amber-700', label: isRTL ? 'أولوية متوسطة' : 'Medium priority' }
                                : { border: 'border-emerald-200', bg: 'bg-emerald-50/50', badge: 'bg-emerald-100 text-emerald-700', label: isRTL ? 'روتيني' : 'Routine' };
                            return (
                                <div key={idx} className={`p-4 rounded-xl border ${style.border} ${style.bg}`} data-testid={`specialist-card-${idx}`}>
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <span className="font-bold text-stone-800 text-sm">
                                            {(isRTL ? sp.specialty_ar : sp.specialty_en) || sp.specialty_ar || sp.specialty_en}
                                        </span>
                                        <Badge className={`${style.badge} border-0 text-xs shrink-0`}>{style.label}</Badge>
                                    </div>
                                    {sp.reason && <p className="text-xs text-stone-600 leading-relaxed">{sp.reason}</p>}
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Nutrition recommendations */}
            {report.nutrition_recommendations && (report.nutrition_recommendations.foods_to_eat?.length > 0 || report.nutrition_recommendations.foods_to_avoid?.length > 0) && (
                <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8 print:shadow-none print:border print:border-stone-200" data-testid="nutrition-recommendations-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-lime-100 flex items-center justify-center">
                                <Salad className="w-4 h-4 text-lime-600" />
                            </div>
                            {isRTL ? 'التوصيات الغذائية' : 'Nutrition Recommendations'}
                        </CardTitle>
                        <p className="text-xs text-stone-500 mt-1">
                            {isRTL
                                ? 'إرشادات غذائية مبنية على نتائج تحاليلك — لا تُغني عن استشارة أخصائي تغذية'
                                : 'Dietary guidance based on your lab results — not a substitute for a dietitian consultation'}
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {report.nutrition_recommendations.foods_to_eat?.length > 0 && (
                                <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-100" data-testid="foods-to-eat">
                                    <p className="font-bold text-emerald-700 text-sm mb-2">
                                        {isRTL ? '✓ أطعمة يُنصح بها' : '✓ Foods to eat'}
                                    </p>
                                    <ul className="space-y-2">
                                        {report.nutrition_recommendations.foods_to_eat.map((f, i) => (
                                            <li key={i} className="text-xs text-stone-700 leading-relaxed">
                                                <span className="font-semibold">{f.item}</span>
                                                {f.reason && <span className="text-stone-500"> — {f.reason}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {report.nutrition_recommendations.foods_to_avoid?.length > 0 && (
                                <div className="p-4 rounded-xl bg-red-50/50 border border-red-100" data-testid="foods-to-avoid">
                                    <p className="font-bold text-red-700 text-sm mb-2">
                                        {isRTL ? '✕ أطعمة يُفضل تقليلها' : '✕ Foods to limit'}
                                    </p>
                                    <ul className="space-y-2">
                                        {report.nutrition_recommendations.foods_to_avoid.map((f, i) => (
                                            <li key={i} className="text-xs text-stone-700 leading-relaxed">
                                                <span className="font-semibold">{f.item}</span>
                                                {f.reason && <span className="text-stone-500"> — {f.reason}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        {report.nutrition_recommendations.general_tips?.length > 0 && (
                            <div className="p-3 rounded-xl bg-stone-50 border border-stone-100" data-testid="nutrition-tips">
                                <p className="font-bold text-stone-700 text-xs mb-1.5">{isRTL ? 'نصائح عامة' : 'General tips'}</p>
                                <ul className="space-y-1">
                                    {report.nutrition_recommendations.general_tips.map((tip, i) => (
                                        <li key={i} className="text-xs text-stone-600">• {tip}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Cross-Analysis Patterns */}
            {report.cross_analysis && report.cross_analysis.length > 0 && (
                <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8 print:shadow-none print:border print:border-stone-200" data-testid="cross-analysis-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <BarChart3 className="w-4 h-4 text-purple-600" />
                            </div>
                            {t('report.patternAnalysis')}
                            <Badge variant="outline" className="ms-auto border-purple-200 text-purple-600">
                                {t('report.patternsDetected', { count: report.cross_analysis.length })}
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {report.cross_analysis.map((pattern, idx) => (
                            <div 
                                key={idx} 
                                className={`p-4 rounded-xl border ${
                                    pattern.risk_level === 'low' ? 'bg-emerald-50/50 border-emerald-100' :
                                    pattern.risk_level === 'moderate' ? 'bg-amber-50/50 border-amber-100' :
                                    pattern.risk_level === 'elevated' ? 'bg-orange-50/50 border-orange-100' :
                                    'bg-red-50/50 border-red-100'
                                }`}
                                data-testid={`cross-analysis-${idx}`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-semibold text-stone-800">{pattern.pattern_name}</h4>
                                    <Badge className={`${
                                        pattern.risk_level === 'low' ? 'bg-emerald-100 text-emerald-700' :
                                        pattern.risk_level === 'moderate' ? 'bg-amber-100 text-amber-700' :
                                        pattern.risk_level === 'elevated' ? 'bg-orange-100 text-orange-700' :
                                        'bg-red-100 text-red-700'
                                    } border-0`}>
                                        {pattern.risk_level === 'low' ? t('report.lowRisk') :
                                         pattern.risk_level === 'moderate' ? t('report.moderateRisk') :
                                         pattern.risk_level === 'elevated' ? t('report.elevatedRisk') :
                                         t('report.highRisk')}
                                    </Badge>
                                </div>
                                <div className="flex flex-wrap gap-1 mb-3">
                                    {pattern.involved_tests?.map((test, i) => (
                                        <Badge key={i} variant="outline" className="text-xs border-stone-200 text-stone-500">
                                            {test}
                                        </Badge>
                                    ))}
                                </div>
                                <p className="text-stone-600 text-sm mb-3">{pattern.interpretation}</p>
                                <div className="pt-3 border-t border-stone-200/50">
                                    <p className="text-sm">
                                        <span className="font-medium text-stone-700">{t('report.recommendation')}: </span>
                                        <span className="text-stone-600">{pattern.recommendation}</span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* AI Summary Card */}
            <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8 print:shadow-none print:border print:border-stone-200" data-testid="ai-summary-card">
                <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <Activity className="w-6 h-6 text-teal-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-stone-800 mb-2">{t('report.aiHealthSummary')}</h3>
                            <p className="text-stone-600 leading-relaxed">{getLocalizedContent('ai_summary')}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* View Mode Toggle - Hidden on Print */}
            <div className="flex items-center gap-4 mb-4 print:hidden">
                <span className="text-sm text-stone-500">View:</span>
                <div className="flex bg-stone-100 rounded-lg p-1">
                    <button
                        onClick={() => setViewMode('grouped')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                            viewMode === 'grouped' 
                                ? 'bg-white shadow-sm text-stone-800' 
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        <BarChart3 className="w-4 h-4 inline mr-1" />
                        By Category
                    </button>
                    <button
                        onClick={() => setViewMode('priority')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                            viewMode === 'priority' 
                                ? 'bg-white shadow-sm text-stone-800' 
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        By Priority
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="results" className="space-y-6">
                <TabsList className="grid grid-cols-3 bg-stone-100 p-1 rounded-xl print:hidden">
                    <TabsTrigger value="results" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                        <FileText className="w-4 h-4 mr-2" />
                        Results
                    </TabsTrigger>
                    <TabsTrigger value="insights" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                        <Lightbulb className="w-4 h-4 mr-2" />
                        Insights
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                        <StickyNote className="w-4 h-4 mr-2" />
                        Notes
                    </TabsTrigger>
                </TabsList>

                {/* Lab Results Tab */}
                <TabsContent value="results" className="print:block">
                    {viewMode === 'grouped' ? (
                        // Grouped by Category View
                        <div className="space-y-6">
                            {Object.entries(groupedValues).map(([category, values]) => (
                                <Card key={category} className="border-0 shadow-lg shadow-stone-200/50 print:shadow-none print:border print:border-stone-200" data-testid={`category-${category}`}>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                                                <BarChart3 className="w-4 h-4 text-teal-600" />
                                            </div>
                                            {category}
                                            <Badge variant="outline" className="ml-auto">{values.length} tests</Badge>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {values.map((value, index) => {
                                                const status = getStatusConfig(value.status);
                                                const StatusIcon = status.icon;
                                                const flag = getValueFlag(value.value, value.reference_range);
                                                const { min, max } = parseReferenceRange(value.reference_range);
                                                const trendData = historicalData[value.name];
                                                
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`${status.bg} ${status.border} border rounded-xl p-4`}
                                                        data-testid={`lab-value-${index}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-3">
                                                                <StatusIcon className={`w-5 h-5 ${status.color}`} />
                                                                <span className="font-medium text-stone-800">{value.name}</span>
                                                                {/* Trend Sparkline */}
                                                                {trendData && trendData.length > 1 && (
                                                                    <Sparkline 
                                                                        data={trendData} 
                                                                        color={status.color.includes('emerald') ? '#10b981' : status.color.includes('amber') ? '#f59e0b' : '#ef4444'}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                {/* High/Low Flag Indicator */}
                                                                {flag && (
                                                                    <div className={`flex items-center gap-1 ${flag === 'high' ? 'text-red-500' : 'text-blue-500'}`}>
                                                                        {flag === 'high' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                                        <span className="text-xs font-medium uppercase">{flag}</span>
                                                                    </div>
                                                                )}
                                                                <span className={`font-bold ${status.color}`}>
                                                                    {value.value} {value.unit}
                                                                </span>
                                                                <Badge variant="outline" className={`${status.bg} ${status.color} ${status.border}`}>
                                                                    {status.label}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Visual Reference Range Bar */}
                                                        <ReferenceRangeBar 
                                                            value={value.value}
                                                            min={min}
                                                            max={max}
                                                            status={value.status}
                                                        />
                                                        
                                                        <div className="mt-3 pt-3 border-t border-stone-200/50">
                                                            <div className="flex items-center justify-between text-sm mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-stone-500">Reference: {value.reference_range}</span>
                                                                    {value.severity && (
                                                                        <Badge variant="outline" className={`text-xs ${
                                                                            value.severity === 'mild' ? 'border-amber-300 text-amber-600' :
                                                                            value.severity === 'moderate' ? 'border-orange-300 text-orange-600' :
                                                                            'border-red-300 text-red-600'
                                                                        }`}>
                                                                            {value.severity}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <Link to={`/trends/${encodeURIComponent(value.name)}`} className="print:hidden">
                                                                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                                                                        <History className="w-3 h-3 mr-1" />
                                                                        History
                                                                    </Button>
                                                                </Link>
                                                            </div>
                                                            {/* Dual-Layer Explanations */}
                                                            <div className="space-y-2">
                                                                {value.simple_explanation && (
                                                                    <p className="text-stone-700 text-sm leading-relaxed">
                                                                        <span className="font-medium">💡 In simple terms:</span> {value.simple_explanation}
                                                                    </p>
                                                                )}
                                                                <details className="group">
                                                                    <summary className="text-xs text-teal-600 cursor-pointer hover:text-teal-700">
                                                                        {value.simple_explanation ? 'Show medical details' : 'Medical explanation'}
                                                                    </summary>
                                                                    <p className="text-stone-600 text-sm leading-relaxed mt-2 pl-2 border-l-2 border-stone-200">
                                                                        {value.explanation}
                                                                    </p>
                                                                </details>
                                                                {/* Follow-up suggestion */}
                                                                {value.suggested_followup && (
                                                                    <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                                                                        <p className="text-xs text-blue-700">
                                                                            <span className="font-medium">📋 Follow-up:</span> {value.suggested_followup}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        // Priority View (Critical first)
                        <Card className="border-0 shadow-lg shadow-stone-200/50 print:shadow-none print:border print:border-stone-200">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                                    Results by Priority
                                    <span className="text-sm font-normal text-stone-500 ml-2">(Critical → Abnormal → Normal)</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Accordion type="multiple" className="space-y-3">
                                    {sortedValues.map((value, index) => {
                                        const status = getStatusConfig(value.status);
                                        const StatusIcon = status.icon;
                                        const flag = getValueFlag(value.value, value.reference_range);
                                        const { min, max } = parseReferenceRange(value.reference_range);
                                        const trendData = historicalData[value.name];
                                        
                                        return (
                                            <AccordionItem
                                                key={index}
                                                value={`item-${index}`}
                                                className={`${status.bg} ${status.border} border rounded-xl px-4`}
                                                data-testid={`lab-value-priority-${index}`}
                                            >
                                                <AccordionTrigger className="hover:no-underline py-4">
                                                    <div className="flex items-center justify-between w-full pr-4">
                                                        <div className="flex items-center gap-3">
                                                            <StatusIcon className={`w-5 h-5 ${status.color}`} />
                                                            <span className="font-medium text-stone-800">{value.name}</span>
                                                            {trendData && trendData.length > 1 && (
                                                                <Sparkline 
                                                                    data={trendData}
                                                                    color={status.color.includes('emerald') ? '#10b981' : status.color.includes('amber') ? '#f59e0b' : '#ef4444'}
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            {flag && (
                                                                <div className={`flex items-center gap-1 ${flag === 'high' ? 'text-red-500' : 'text-blue-500'}`}>
                                                                    {flag === 'high' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                                    <span className="text-xs font-medium uppercase">{flag}</span>
                                                                </div>
                                                            )}
                                                            <span className={`font-bold ${status.color}`}>
                                                                {value.value} {value.unit}
                                                            </span>
                                                            <Badge variant="outline" className={`${status.bg} ${status.color} ${status.border}`}>
                                                                {status.label}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </AccordionTrigger>
                                                <AccordionContent className="pb-4">
                                                    <div className="pl-8 space-y-3">
                                                        <ReferenceRangeBar 
                                                            value={value.value}
                                                            min={min}
                                                            max={max}
                                                            status={value.status}
                                                        />
                                                        <div className="flex items-center gap-2 text-sm text-stone-600">
                                                            <span className="font-medium">Reference Range:</span>
                                                            <span>{value.reference_range}</span>
                                                        </div>
                                                        <p className="text-stone-700 leading-relaxed">{value.explanation}</p>
                                                        <Link to={`/trends/${encodeURIComponent(value.name)}`}>
                                                            <Button variant="outline" size="sm" className="rounded-full mt-2">
                                                                <TrendingUp className="w-4 h-4 mr-1" />
                                                                View Full Trend
                                                            </Button>
                                                        </Link>
                                                    </div>
                                                </AccordionContent>
                                            </AccordionItem>
                                        );
                                    })}
                                </Accordion>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Health Insights Tab */}
                <TabsContent value="insights">
                    <div className="grid gap-6 md:grid-cols-2">
                        <Card className="border-0 shadow-lg shadow-stone-200/50" data-testid="health-insights-card">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Lightbulb className="w-5 h-5 text-amber-500" />
                                    {t('report.healthInsights')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {getLocalizedArray('health_insights').map((insight, index) => (
                                        <div
                                            key={index}
                                            className="flex items-start gap-3 p-4 bg-amber-50/50 rounded-xl border border-amber-100"
                                            data-testid={`insight-${index}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                                <span className="text-amber-700 font-semibold text-sm">{index + 1}</span>
                                            </div>
                                            <p className="text-stone-700 pt-1">{insight}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-lg shadow-stone-200/50" data-testid="recommendations-card">
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ClipboardList className="w-5 h-5 text-teal-600" />
                                    {t('report.recommendedActions')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {getLocalizedArray('recommendations').map((rec, index) => (
                                        <div
                                            key={index}
                                            className="flex items-start gap-3 p-4 bg-teal-50/50 rounded-xl border border-teal-100"
                                            data-testid={`recommendation-${index}`}
                                        >
                                            <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                            <p className="text-stone-700">{rec}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Clinical Notes Tab */}
                <TabsContent value="notes">
                    <Card className="border-0 shadow-lg shadow-stone-200/50" data-testid="notes-card">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <StickyNote className="w-5 h-5 text-purple-500" />
                                Clinical Notes
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <Textarea
                                    placeholder="Add your notes here... (e.g., symptoms, medications, doctor's comments)"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="min-h-[200px] resize-none"
                                    data-testid="notes-textarea"
                                />
                                <div className="flex justify-end">
                                    <Button 
                                        onClick={handleSaveNotes}
                                        className="bg-teal-500 hover:bg-teal-600"
                                        data-testid="save-notes-btn"
                                    >
                                        Save Notes
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* QR Code Section */}
            <div className="mt-8 flex items-center justify-center gap-4 p-4 bg-stone-50 rounded-2xl print:bg-white print:border print:border-stone-200">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center border border-stone-200">
                    <QrCode className="w-10 h-10 text-stone-400" />
                </div>
                <div className="text-center">
                    <p className="text-sm text-stone-600">Scan to verify this report</p>
                    <p className="text-xs text-stone-400 font-mono">{reportId.slice(0, 12).toUpperCase()}</p>
                </div>
            </div>

            {/* Dynamic Medical Disclaimer */}
            <div className={`mt-8 p-4 rounded-2xl print:bg-white print:border ${
                report.urgency_level === 'urgent' ? 'bg-red-50 border border-red-200' :
                report.urgency_level === 'prompt_review' ? 'bg-amber-50 border border-amber-200' :
                report.urgency_level === 'monitor' ? 'bg-blue-50 border border-blue-200' :
                'bg-stone-100'
            }`}>
                <p className={`text-sm ${
                    report.urgency_level === 'urgent' ? 'text-red-700' :
                    report.urgency_level === 'prompt_review' ? 'text-amber-700' :
                    report.urgency_level === 'monitor' ? 'text-blue-700' :
                    'text-stone-600'
                }`}>
                    {getLocalizedContent('medical_disclaimer') || (
                        <>
                            ⚠️ <strong>{t('report.disclaimer')}:</strong> {t('report.disclaimerText')}
                        </>
                    )}
                </p>
            </div>

            {/* Share Dialog */}
            <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-teal-500" />
                            {t('report.shareReport')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('report.shareSubtitle')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Secure link via WhatsApp (no login needed for recipient) */}
                        <div className="rounded-xl border border-[#25D366]/40 bg-[#25D366]/5 p-4 space-y-3">
                            <p className="text-sm font-medium text-stone-700 flex items-center gap-2">
                                <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                {isRTL ? 'إرسال رابط التقرير عبر واتساب' : 'Send report link via WhatsApp'}
                            </p>
                            <p className="text-xs text-stone-500">
                                {isRTL
                                    ? 'المستلم يفتح التقرير مباشرة بدون تسجيل دخول — الرابط صالح لمدة 7 أيام'
                                    : 'The recipient opens the report without login — link valid for 7 days'}
                            </p>
                            <div className="flex gap-2">
                                <Input
                                    type="tel"
                                    dir="ltr"
                                    placeholder={isRTL ? 'رقم الجوال (اختياري) مثل 05xxxxxxxx' : 'Phone (optional) e.g. 05xxxxxxxx'}
                                    value={sharePhone}
                                    onChange={(e) => setSharePhone(e.target.value)}
                                    data-testid="share-whatsapp-phone-input"
                                />
                                <Button
                                    onClick={handleWhatsAppLinkShare}
                                    disabled={sharingLink}
                                    className="bg-[#25D366] hover:bg-[#20BD5A] text-white shrink-0"
                                    data-testid="share-whatsapp-link-btn"
                                >
                                    {sharingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRTL ? 'إرسال' : 'Send')}
                                </Button>
                            </div>
                        </div>

                        {/* Quick Share Buttons */}
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                onClick={handleWhatsAppShare}
                                disabled={sharingWhatsApp}
                                className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
                                data-testid="share-whatsapp-btn"
                            >
                                {sharingWhatsApp ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Preparing...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                        </svg>
                                        WhatsApp (PDF)
                                    </>
                                )}
                            </Button>
                            <Button
                                onClick={() => {
                                    const reportUrl = `${window.location.origin}/report/${reportId}`;
                                    const subject = encodeURIComponent(`Lab Report - ${report?.panel_type || 'Medical Results'}`);
                                    const body = encodeURIComponent(`Please find my lab report:\n\n${reportUrl}\n\nReport ID: ${reportId.slice(0, 8).toUpperCase()}\nDate: ${report?.upload_date ? new Date(report.upload_date).toLocaleDateString() : 'N/A'}`);
                                    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                                }}
                                variant="outline"
                                className="border-stone-200"
                                data-testid="share-email-app-btn"
                            >
                                <Mail className="w-5 h-5 mr-2 text-stone-600" />
                                Email App
                            </Button>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-white px-2 text-stone-500">Or send via email</span>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Recipient Email</label>
                            <div className="flex gap-2">
                                <Input
                                    type="email"
                                    placeholder="doctor@clinic.com"
                                    value={shareEmail}
                                    onChange={(e) => setShareEmail(e.target.value)}
                                    data-testid="share-email-input"
                                />
                                <Button onClick={handleShare} className="bg-teal-500 hover:bg-teal-600">
                                    Send
                                </Button>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-white px-2 text-stone-500">{isRTL ? 'أو انسخ الرابط الآمن' : 'Or copy secure link'}</span>
                            </div>
                        </div>

                        <Button
                            variant="outline"
                            className="w-full border-stone-200"
                            onClick={handleCopySecureLink}
                            data-testid="copy-secure-link-btn"
                        >
                            <Copy className="w-4 h-4 mr-2" />
                            {isRTL ? 'نسخ رابط آمن للمشاركة (صالح 7 أيام)' : 'Copy secure share link (valid 7 days)'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body { background: white !important; }
                    .print\\:hidden { display: none !important; }
                    .print\\:block { display: block !important; }
                    .print\\:shadow-none { box-shadow: none !important; }
                    .print\\:border { border: 1px solid #e5e7eb !important; }
                    .print\\:bg-white { background: white !important; }
                }
            `}</style>
        </div>
    );
};

export default ReportPage;
